import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as bookActions from "../../redux/actions/bookActions";
import { ROLES } from "../common/Const";
import { buildSchoolYearRange } from "../common/schoolYear";
import { canManageClassStudents, getEffectivePermissionRole } from "./rbac";
import {
  canEditLessonAttendance,
  canUndoWholeLessonCancellation,
  canUseAdministrativeLessonCommands,
  getAdministrativeLessonCommandBlockReason,
} from "./lessonCommandPermissions";
import { buildLessonRegisterCommandBasePayload } from "./lessonRegisterPayloads";
import {
  ATTENDANCE_DECISIONS,
  applyAttendanceDecision,
  buildAttendanceDraft,
  buildAttendanceSavePayload,
  getAttendanceSummary,
  markAllPresent,
  rebuildAttendanceDraftPreservingDecisions,
  validateAttendanceDraft,
} from "./attendanceDraft";
import { getAttendanceLessonViewModel } from "./attendanceLessonSelectors";
import AttendanceLessonHeader from "./AttendanceLessonHeader";
import AttendanceSummaryBar from "./AttendanceSummaryBar";
import AttendanceDecisionList from "./AttendanceDecisionList";
import AttendanceSaveBar from "./AttendanceSaveBar";
import AttendanceCommandModals from "./AttendanceCommandModals";

export function AttendancePageBase({
  match,
  history,
  schoolClasses,
  teachers,
  students,
  classStudents,
  lessonRegister,
  books,
  activeSchoolYearStartYear,
  userRole,
  userTeacherId,
  loadSchoolClasses,
  loadTeachers,
  loadStudents,
  loadClassStudents,
  loadLessonRegisters,
  loadBooks,
  saveLessonRegister,
  reportLessonAbsence,
  cancelWholeLesson,
  undoWholeLessonCancellation,
  addClassStudent,
  removeClassStudent,
}) {
  const { classId, lessonIndex } = match.params;
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [activeAction, setActiveAction] = useState("");
  const [draftKey, setDraftKey] = useState("");
  const [draftParticipantsKey, setDraftParticipantsKey] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingCancelledPresence, setPendingCancelledPresence] = useState(null);
  const unblockNavigationRef = useRef(null);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
    if (teachers.length === 0) loadTeachers();
    if (students.length === 0) loadStudents();
    if (books.length === 0) loadBooks();
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    books.length,
    students.length,
    teachers.length,
    loadBooks,
    loadClassStudents,
    loadLessonRegisters,
    loadSchoolClasses,
    loadStudents,
    loadTeachers,
  ]);

  const viewModel = useMemo(() => getAttendanceLessonViewModel({
    schoolClasses,
    teachers,
    students,
    classStudents,
    lessonRegister,
    books,
    classId,
    lessonIndex,
  }), [schoolClasses, teachers, students, classStudents, lessonRegister, books, classId, lessonIndex]);

  const nextDraftKey = viewModel.status === "ready"
    ? [
      classId,
      lessonIndex,
      (viewModel.scheduledLesson.lessonRegister && viewModel.scheduledLesson.lessonRegister.id) || "",
      viewModel.lessonDetails.attendanceStatus || "",
      viewModel.lessonDetails.lessonStatus || "",
    ].join(":")
    : "";
  const nextDraftParticipantsKey = viewModel.status === "ready"
    ? viewModel.participants.map(participant => participant.id).sort().join(":")
    : "";

  useEffect(() => {
    if (viewModel.status === "ready" && (
      !dirty ||
      nextDraftKey !== draftKey ||
      nextDraftParticipantsKey !== draftParticipantsKey
    )) {
      const identityChanged = nextDraftKey !== draftKey;
      const participantsChanged = nextDraftParticipantsKey !== draftParticipantsKey;

      setDraft(currentDraft => (
        dirty && participantsChanged && !identityChanged
          ? rebuildAttendanceDraftPreservingDecisions(currentDraft, viewModel.scheduledLesson)
          : buildAttendanceDraft(viewModel.scheduledLesson)
      ));
      setDirty(dirty && participantsChanged && !identityChanged);
      setError("");
      setValidationAttempted(false);
      setDraftKey(nextDraftKey);
      setDraftParticipantsKey(nextDraftParticipantsKey);
    }
  }, [
    dirty,
    draftKey,
    draftParticipantsKey,
    nextDraftKey,
    nextDraftParticipantsKey,
    viewModel.status,
    viewModel.scheduledLesson,
  ]);

  useEffect(() => {
    if (!dirty || !history.block) {
      return undefined;
    }

    const unblockNavigation = history.block((nextLocation, action) => {
      const currentLocation = history.location || {};
      const isSameLocation = nextLocation.pathname === currentLocation.pathname &&
        nextLocation.search === currentLocation.search &&
        nextLocation.hash === currentLocation.hash;

      if (isSameLocation) {
        return true;
      }

      setPendingNavigation({ action, location: nextLocation });
      return false;
    });
    unblockNavigationRef.current = unblockNavigation;

    return () => {
      unblockNavigation();
      if (unblockNavigationRef.current === unblockNavigation) {
        unblockNavigationRef.current = null;
      }
    };
  }, [dirty, history]);

  if (viewModel.status !== "ready") {
    return (
      <div className="attendance-page" role="alert">
        Nie znaleziono zajęć.
      </div>
    );
  }

  if (!draft) {
    return <div className="attendance-page">Ładowanie obecności...</div>;
  }

  const canEdit = canEditLessonAttendance(userRole, userTeacherId, viewModel.lessonDetails) &&
    viewModel.lessonDetails.lessonStatus !== "cancelled";
  const canUseAdministrativeCommands = canUseAdministrativeLessonCommands({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const administrativeCommandBlockReason = getAdministrativeLessonCommandBlockReason({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const canUndoCancellation = canUndoWholeLessonCancellation({
    role: userRole,
    lesson: viewModel.scheduledLesson,
    lessonDetails: viewModel.lessonDetails,
  });
  const canEditLessonDetails = userRole !== ROLES.TEACHER;
  const canEditLessonComment = canEditLessonAttendance(userRole, userTeacherId, viewModel.lessonDetails) &&
    viewModel.lessonDetails.lessonStatus !== "cancelled";
  const summary = getAttendanceSummary(draft);
  const validation = validateAttendanceDraft(draft);
  const invalidStudentIds = validationAttempted ? validation.unmarkedStudentIds : [];

  const confirmNavigation = () => {
    const navigation = pendingNavigation;

    if (!navigation) {
      return;
    }

    setPendingNavigation(null);
    setDirty(false);

    if (unblockNavigationRef.current) {
      unblockNavigationRef.current();
      unblockNavigationRef.current = null;
    }

    if (navigation.action === "REPLACE") {
      history.replace(navigation.location);
      return;
    }

    if (navigation.action === "POP" || !navigation.location) {
      history.goBack();
      return;
    }

    history.push(navigation.location);
  };

  const cancelNavigation = () => {
    setPendingNavigation(null);
  };

  const handleReturn = () => {
    if (dirty) {
      setPendingNavigation({ action: "POP", location: null });
      return;
    }

    history.goBack();
  };

  const handleDecisionChange = (studentId, decision) => {
    const currentDecision = draft.decisions[studentId];
    const isCancelledDecision = currentDecision && (
      currentDecision.decision === ATTENDANCE_DECISIONS.CANCELLED_ON_TIME ||
      currentDecision.decision === ATTENDANCE_DECISIONS.CANCELLED_LATE
    );

    if (isCancelledDecision && decision === ATTENDANCE_DECISIONS.PRESENT) {
      const student = draft.participants.find(participant => participant.id === studentId);
      setPendingCancelledPresence({
        studentId,
        studentName: student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "",
      });
      return;
    }

    setDraft(currentDraft => applyAttendanceDecision(currentDraft, studentId, decision));
    setDirty(true);
    setError("");
  };

  const confirmCancelledPresence = () => {
    const pendingPresence = pendingCancelledPresence;

    if (!pendingPresence) {
      return;
    }

    setDraft(currentDraft => applyAttendanceDecision(
      currentDraft,
      pendingPresence.studentId,
      ATTENDANCE_DECISIONS.PRESENT,
    ));
    setPendingCancelledPresence(null);
    setDirty(true);
    setError("");
  };

  const cancelCancelledPresence = () => {
    setPendingCancelledPresence(null);
  };

  const handleMarkAllPresent = () => {
    setDraft(currentDraft => markAllPresent(currentDraft));
    setDirty(true);
    setError("");
    setValidationAttempted(false);
  };

  const handleSave = () => {
    const nextValidation = validateAttendanceDraft(draft);
    if (!nextValidation.isValid) {
      setValidationAttempted(true);
      setError(`Sprawdź obecność dla wszystkich uczniów. Pozostało ${nextValidation.unmarkedCount} niezaznaczonych.`);
      return;
    }

    const payload = buildAttendanceSavePayload(draft, userRole, userTeacherId);
    if (!payload) {
      setValidationAttempted(false);
      setError("Brak uprawnień do zapisu obecności.");
      return;
    }

    setSaving(true);
    setError("");
    setValidationAttempted(false);
    saveLessonRegister(payload)
      .then(() => {
        setSaving(false);
        setDirty(false);
        setValidationAttempted(false);
        history.goBack();
      })
      .catch((saveError) => {
        setSaving(false);
        setValidationAttempted(false);
        setError(saveError.message || "Nie udało się zapisać obecności.");
      });
  };

  const getCommandStudents = () => draft.participants.map((participant) => {
    const decisionEntry = draft.decisions[participant.id] || {};
    const { decision } = decisionEntry;
    const cancelled = decision === "cancelledOnTime" || decision === "cancelledLate";

    return {
      id: participant.id,
      isPresent: decision === "present",
      ...(cancelled ? { participationStatus: decision } : {}),
      ...(decisionEntry.cancellation ? { cancellation: decisionEntry.cancellation } : {}),
    };
  });

  const getPreservedCommandStudents = () => (
    viewModel.scheduledLesson.lessonRegister
      ? viewModel.scheduledLesson.lessonRegister.students || []
      : getCommandStudents()
  );

  const ensureLessonRegisterForCommand = (lessonDetails) => {
    if (viewModel.scheduledLesson.lessonRegister) {
      return Promise.resolve(viewModel.scheduledLesson.lessonRegister);
    }

    return Promise.resolve({
      id: viewModel.scheduledLesson.id,
      lessonId: viewModel.scheduledLesson.id,
      lessonDetails,
      students: getCommandStudents(),
    });
  };

  const handleUpdateLessonDetails = (lessonDetails) => {
    const nextLessonDetails = {
      ...viewModel.lessonDetails,
      ...lessonDetails,
    };
    const commandPayload = buildLessonRegisterCommandBasePayload({
      lesson: viewModel.scheduledLesson,
      lessonDetailsEx: nextLessonDetails,
      studentsEx: getPreservedCommandStudents(),
    });

    if (!canEditLessonDetails) {
      return saveLessonRegister({
        ...commandPayload,
        lessonDetails: {
          comment: nextLessonDetails.comment || "",
        },
      });
    }

    return saveLessonRegister(commandPayload);
  };

  const handleReportAbsence = command => (
    ensureLessonRegisterForCommand(viewModel.lessonDetails)
      .then(savedRegister => reportLessonAbsence({
        lessonId: savedRegister.id,
        ...command,
      }))
  );

  const handleCancelWholeLesson = command => (
    ensureLessonRegisterForCommand(viewModel.lessonDetails)
      .then(savedRegister => cancelWholeLesson({
        lessonId: savedRegister.id,
        ...command,
      }))
  );

  const handleUndoWholeLessonCancellation = () => undoWholeLessonCancellation({
    lessonId: viewModel.scheduledLesson.lessonRegister.id,
  });

  const handleAddStudentToClass = studentId => (
    addClassStudent({
      schoolClassId: classId,
      studentId,
    })
  );

  const handleRemoveStudentFromClass = (studentId) => {
    const classStudent = classStudents.find(item => (
      item.schoolClassId === classId && item.studentId === studentId
    ));

    if (!classStudent) {
      return Promise.resolve();
    }

    return removeClassStudent(classStudent);
  };

  return (
    <div className="attendance-page">
      <div className="attendance-page__topbar">
        <button type="button" className="btn manage-button manage-teal-button" onClick={handleReturn}>
          <span aria-hidden="true" className="attendance-page__return-icon">←</span>
          Powrót
        </button>
      </div>
      <AttendanceLessonHeader
        canUndoWholeLessonCancellation={canUndoCancellation}
        viewModel={viewModel}
        onAction={setActiveAction}
      />
      {viewModel.lessonDetails.lessonStatus === "cancelled" && (
        <div className="alert alert-secondary" role="status">
          Lekcja odwołana
        </div>
      )}
      {error && <div className="alert alert-danger attendance-page__validation-alert" role="alert">{error}</div>}
      <AttendanceSummaryBar summary={summary} canEdit={canEdit} onMarkAllPresent={handleMarkAllPresent} />
      <AttendanceDecisionList
        participants={draft.participants}
        decisions={draft.decisions}
        invalidStudentIds={invalidStudentIds}
        lessonDetails={viewModel.lessonDetails}
        canEdit={canEdit}
        onDecisionChange={handleDecisionChange}
      />
      <AttendanceSaveBar
        canEdit={canEdit}
        dirty={dirty}
        validation={validation}
        validationAttempted={validationAttempted}
        saving={saving}
        onSave={handleSave}
        onReturn={handleReturn}
      />
      {pendingNavigation && (
        <div className="attendance-page__modal-placeholder" role="dialog" aria-modal="true" aria-labelledby="attendance-leave-title">
          <div className="attendance-page__modal-card">
            <h3 id="attendance-leave-title" className="attendance-page__modal-title">Anulować niezapisane zmiany?</h3>
            <p className="attendance-page__modal-text">
              Masz niezapisane zmiany obecności. Jeśli opuścisz ten ekran, nie zostaną zachowane.
            </p>
            <div className="attendance-page__modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={cancelNavigation}>
                Zostań na stronie
              </button>
              <button type="button" className="btn manage-button manage-red-button" onClick={confirmNavigation}>
                Anuluj zmiany
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingCancelledPresence && (
        <div
          className="attendance-page__modal-placeholder"
          role="dialog"
          aria-modal="true"
          aria-labelledby="attendance-cancelled-presence-title"
        >
          <div className="attendance-page__modal-card">
            <h3 id="attendance-cancelled-presence-title" className="attendance-page__modal-title">
              Potwierdź obecność odwołanego ucznia
            </h3>
            <p className="attendance-page__modal-text">
              {pendingCancelledPresence.studentName || "Uczeń"} odwołał zajęcia.
              Czy na pewno chcesz oznaczyć, że przyszedł na lekcję?
            </p>
            <div className="attendance-page__modal-footer">
              <button type="button" className="btn manage-button manage-teal-button" onClick={cancelCancelledPresence}>
                Zostaw odwołanie
              </button>
              <button type="button" className="btn manage-button manage-red-button" onClick={confirmCancelledPresence}>
                Potwierdź obecność
              </button>
            </div>
          </div>
        </div>
      )}
      <AttendanceCommandModals
        activeAction={activeAction}
        viewModel={viewModel}
        teachers={teachers}
        canEditLessonComment={canEditLessonComment}
        canEditLessonDetails={canEditLessonDetails}
        canManageStudents={canManageClassStudents(userRole)}
        allStudents={students}
        classStudents={classStudents}
        canUseAdministrativeCommands={canUseAdministrativeCommands}
        administrativeCommandBlockReason={administrativeCommandBlockReason}
        canUndoWholeLessonCancellation={canUndoCancellation}
        onClose={() => setActiveAction("")}
        onUpdateLessonDetails={handleUpdateLessonDetails}
        onAddStudent={handleAddStudentToClass}
        onRemoveStudent={handleRemoveStudentFromClass}
        onReportAbsence={handleReportAbsence}
        onCancelWholeLesson={handleCancelWholeLesson}
        onUndoWholeLessonCancellation={handleUndoWholeLessonCancellation}
      />
    </div>
  );
}

AttendancePageBase.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      classId: PropTypes.string.isRequired,
      lessonIndex: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    block: PropTypes.func,
    goBack: PropTypes.func.isRequired,
    location: PropTypes.object,
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  classStudents: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  books: PropTypes.array.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  loadSchoolClasses: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  loadBooks: PropTypes.func.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  reportLessonAbsence: PropTypes.func.isRequired,
  cancelWholeLesson: PropTypes.func.isRequired,
  undoWholeLessonCancellation: PropTypes.func.isRequired,
  addClassStudent: PropTypes.func.isRequired,
  removeClassStudent: PropTypes.func.isRequired,
};

AttendancePageBase.defaultProps = {
  userRole: "",
  userTeacherId: "",
};

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  books: state.books,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
  userRole: getEffectivePermissionRole(state.user),
  userTeacherId: state.user.teacherId,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  loadBooks: bookActions.loadBooks,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  reportLessonAbsence: lessonRegisterActions.reportLessonAbsence,
  cancelWholeLesson: lessonRegisterActions.cancelWholeLesson,
  undoWholeLessonCancellation: lessonRegisterActions.undoWholeLessonCancellation,
  addClassStudent: classStudentsActions.addClassStudent,
  removeClassStudent: classStudentsActions.removeClassStudent,
};

export default connect(mapStateToProps, mapDispatchToProps)(AttendancePageBase);
